import type { AppLanguage, AppLanguageConfig } from './types';

export const LANGUAGE_STORAGE_KEY = 'ofrontend.appLanguage';

export const DEFAULT_LANGUAGE: AppLanguage = 'he';

/** Registry of enabled languages — extend this list for new locales. */
export const APP_LANGUAGES: readonly AppLanguageConfig[] = [
  {
    code: 'he',
    nativeName: 'עברית',
    pickerIcon: 'flag-il',
    dir: 'rtl',
  },
  {
    code: 'en',
    nativeName: 'English',
    pickerIcon: 'flag-us',
    dir: 'ltr',
  },
] as const;

const languageByCode = new Map(APP_LANGUAGES.map((l) => [l.code, l]));

export function getLanguageConfig(code: AppLanguage): AppLanguageConfig {
  const found = languageByCode.get(code);
  if (!found) {
    return languageByCode.get(DEFAULT_LANGUAGE)!;
  }
  return found;
}

export function isAppLanguage(value: string): value is AppLanguage {
  return languageByCode.has(value as AppLanguage);
}

export function readStoredLanguage(): AppLanguage {
  try {
    const raw = localStorage.getItem(LANGUAGE_STORAGE_KEY)?.trim();
    if (raw && isAppLanguage(raw)) {
      return raw;
    }
  } catch {
    /* private mode */
  }
  return DEFAULT_LANGUAGE;
}

export function writeStoredLanguage(code: AppLanguage): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    /* private mode */
  }
}

/** Cycle to the next enabled locale (wraps around). */
export function nextAppLanguage(current: AppLanguage): AppLanguage {
  const index = APP_LANGUAGES.findIndex((entry) => entry.code === current);
  if (index < 0) {
    return DEFAULT_LANGUAGE;
  }
  const next = APP_LANGUAGES[(index + 1) % APP_LANGUAGES.length];
  return next?.code ?? DEFAULT_LANGUAGE;
}

/** BCP 47 tag for `Intl` formatters — matches app language, not the browser default. */
export function localeTagForAppLanguage(language: AppLanguage): string {
  return language === 'he' ? 'he-IL' : 'en-US';
}
